import { emdClient } from "@/utils/apiClients"

export type BondSpreadHistory = Array<{ day: string; zSpread: number | null }>
export type SwapSeries = Array<{ day: string; rate: number | null }>

export interface YearOfSwap {
  year: number
}

export interface CorpBond {
  isin: string
  bondName: string
  countryCode: string
  year: string
  bondType: string
  emdSector: string | null
  issuer_id: string | null
}

export interface BondSpread {
  isin: string
  bondName: string
  year: string
  day: string
  zSpread: number | null
  code: string
}

export type BondSpreadSeries = Array<BondSpread>

export interface BondInfo {
  isin: string
  bondName: string
  countryCode: string
  year: string
  bondType: string
  emdSector: string
  issuer_id: number
}

export interface IssuerInfo {
  issuer_id: number
  issuer_name: string
  emd_sector: string
  country: string
  is_financial: boolean
  ticker: string
  sub_credit_bucket: string
  isins: string
}

export interface IssuerInfoWithDetail {
  issuer_id: number
  issuer_name: string
  emd_sector: string
  country: string
  is_financial: boolean
  ticker: string
  sub_credit_bucket: string
  isins: string
  model_initiated: number
  "crm.business_profile_score": number | null
  "crm.debt_structure_score": number | null
  "crm.financials_score": number | null
  "crm.mgmt_strategy_score": number | null
  "crm.sovereign_score": number | null
  "esg.environmental": number | null
  "esg.social": number | null
  "esg.governance": number | null
  "esg.incident_management": string
  "esg.outlook": string
  analyst_primary: string
  analyst_backup: string
  crm_last_updated: string
  esg_last_updated: string
  "crm.score": number | null
  crm_quartile: number | null
  "esg.score": string //api return string
  esg_quintile: number | number
}

export interface BondInfoWithDetail {
  isin: string
  emd_sector: string
  country_code: string
  issuer_id: number
  issuer_name: string
  bam_id: string
  ticker: string
  maturity_date: string
  coupon_amt: number
  coupon_freq: string
  instrument: string
  country: string
  region: string
  sub_credit_bucket: string
  spread_duration: number | null
  z_spread: number | null
  "crm.score": number | null
  crm_quartile: number | null
  "esg.score": string
  esg_quintile: number | null
}

export default class CorpApi {
  private getApiClient() {
    return emdClient!
  }

  private async safeGet<T>(url: string): Promise<T> {
    const api = this.getApiClient()
    try {
      const { status, data } = await api.get<T>(url)
      if (status === 200) return data
      throw new Error(`Request failed[${url}]: ${status}`)
    } catch (e) {
      console.error(e)
      throw new Error("API request failed")
    }
  }

  async loadCorpIssuers(modelOnly = false, sector: string = "all") {
    const query = []
    if (sector && sector !== "all") {
      query.push(`emd_sector=${encodeURIComponent(sector)}`)
    }
    if (modelOnly) {
      query.push("model_initiated=true")
    }

    const url = `/api/corp_issuers?${query.join("&")}`
    return this.safeGet<IssuerInfoWithDetail[]>(url)
  }

  async loadIssuerInfo(issuerId: string | number) {
    const url = "/api/corp_issuers/" + issuerId
    return this.safeGet<IssuerInfo>(url)
  }

  async loadIsinsInfo(isins: string) {
    const url = `/api/corpbonds/${isins}`
    return this.safeGet<BondInfo[]>(url)
  }

  async loadCorpBonds(sector: string) {
    const url = `/api/corp_bonds?${sector && sector !== "all" ? `emd_sector=${encodeURIComponent(sector)}` : ""}`
    return this.safeGet<BondInfoWithDetail[]>(url)
  }

  async loadBondSeries(ISIN: string) {
    const url = `/api/zspreadToWorst/${ISIN}`
    const data = await this.safeGet<BondSpreadHistory>(url)
    return data
      .map((d) => [Date.parse(d.day), d.zSpread] as const)
      .filter(([time]) => !isNaN(time))
      .sort((a, b) => a[0] - b[0])
  }

  async loadAggSeries(instrument: string) {
    const url = `/api/zspreadByCorpSub/${instrument}`
    const data = await this.safeGet<BondSpreadHistory>(url)
    return data
      .map((d) => [Date.parse(d.day), d.zSpread] as const)
      .filter(([time]) => !isNaN(time))
      .sort((a, b) => a[0] - b[0])
  }

  async loadDefaultBonds(issuer: string): Promise<[string?, string?]> {
    const modelURL = `/api/corp_issuers/${issuer}?fields=default_bond_1,default_bond_2`
    const data = await this.safeGet<{ field: string; value: string }[]>(modelURL)
    const bond1 = (data ?? []).find((d) => d.field === "default_bond_1")
    const bond2 = (data ?? []).find((d) => d.field === "default_bond_2")
    return [bond1?.value, bond2?.value]
  }

  async updateDefaultBonds(issuer: string, bond1: string, bond2: string | null = null) {
    if (!issuer || !bond1) {
      throw new Error("Issuer and bond1 must be provided")
    }

    const api = this.getApiClient()
    const modelURL = `/api/corp_issuers/${issuer}`
    const batch = []
    const p1 = api.patch(modelURL, { default_bond_1: bond1 })
    batch.push(p1)
    if (bond2) {
      const p2 = api.patch(modelURL, { default_bond_2: bond2 })
      batch.push(p2)
    }

    try {
      await Promise.all(batch)
    } catch {
      throw new Error("can not update default bond")
    }
  }

  async loadCorpSectors() {
    const url = "/api/corp-sectors"
    return this.safeGet<string[]>(url)
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  async loadCorpDoc(docPath: string): Promise<{ ops: Array<any> }> {
    const api = this.getApiClient()
    const { data, status } = await api.get(docPath)
    if (status === 200 && data) {
      return typeof data === "string" ? { ops: [{ insert: data }] } : data
    } else {
      throw new Error("can not load corp doc path")
    }
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  async saveCorpDoc(resourcePath: string, remoteKey: string, newContent: any) {
    const api = this.getApiClient()
    const { status } = await api.patch(resourcePath, {
      path: remoteKey,
      value: newContent
    })
    return status
  }

  async loadBondTs(isin: string) {
    const url = `/api/zspread/${isin}`
    const data = await this.safeGet<BondSpreadSeries>(url)
    return data.map((d) => [Date.parse(d.day), d.zSpread ?? NaN])
  }

  async loadYearOfSwaps() {
    const url = `/api/yearsOfSwap`
    return this.safeGet<YearOfSwap[]>(url)
  }

  async loadSwap(year: string | number) {
    const data = await this.safeGet<SwapSeries>(`/api/swapseries/${year}`)
    return data.map((d) => [Date.parse(d.day), d.rate ?? NaN])
  }

  async loadBondsByRegion(regionName: string) {
    const url = `/api/bond/${regionName}`
    return this.safeGet<Array<{ isin: string; bondName: string; bondType: string; year: string }>>(url)
  }

  async loadAllCorpBonds() {
    const url = "/api/corpbonds/ALL"
    return this.safeGet<CorpBond[]>(url)
  }

  async loadCrmModelResult(issuer: string) {
    const url = `/api/corp_crm_models/${issuer}`
    //too complex messy structure, use any for now
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.safeGet<any>(url)
  }

  async loadCrmModelComponent(issuer: string, component: string) {
    const url = `/api/corp_crm_models/${issuer}?path=${component}`
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.safeGet<any>(url)
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  async patchCrmModelComponent(issuer: string, component: string, value: any, updateCrmDate = true) {
    const api = this.getApiClient()
    const url = `/api/corp_crm_models/${issuer}`
    try {
      const { status } = await api.patch(url, { path: component, value, update_crm_date: updateCrmDate })
      if (status === 200) return true
      throw new Error(`Patch failed for ${component} with status ${status}`)
    } catch (e) {
      console.error(e)
      throw new Error("Failed to patch CRM model component")
    }
  }

  async loadCrmModelTemplate(isFinancial = false) {
    const url = `/api/corp_crm_template?is_financial=${isFinancial}`
    //too complex messy structure, use any for now
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.safeGet<any>(url)
  }

  async setIssuerCoverStatus(issuerId: string | number, analystPrimary: string, analystBackup: string) {
    const api = this.getApiClient()
    const { status } = await api.post(`/api/corp_crm_models/${issuerId}`)
    if (status === 200) {
      try {
        const modelURL = `/api/corp_crm_models/${issuerId}`
        const p1 = api.patch(modelURL, { path: "analyst_primary", value: analystPrimary })
        const p2 = api.patch(modelURL, { path: "analyst_backup", value: analystBackup })
        await Promise.all([p1, p2])
      } catch {
        throw new Error(`failed to update analyst for issuer ${issuerId}`)
      }
    } else {
      throw new Error(`something wrong happnened when updating the coverage status for issuer ${issuerId}`)
    }
  }
}
